import { IconNames } from '@blueprintjs/icons';
import {
    formatDate,
    formatGender,
    formatNameLastFirst,
    getProductName,
} from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { Dialog } from '@strata/react-dialog';
import { FormSectionRow } from '@strata/react-form-section';
import { Hyperlink } from '@strata/react-hyperlink';
import { InlineMessage } from '@strata/react-inline-message';
import { PhoneInputFormik } from '@strata/react-inputs';
import {
    AddressDetailLabelValue,
    DetailLabel,
    DetailLabelValuePair,
} from '@strata/react-label-value-pairs';
import { IntakeStatusIndicator, TreatmentStatusIndicator } from '@strata/react-status-indicators';
import { useFormikContext } from 'formik';
import { type ComponentProps, type ReactElement } from 'react';
import type * as Yup from 'yup';

import { LighthouseIntakeStatusIndicator } from 'components/shared/LighthouseIntakeStatusIndicator';
import { AgoraTreatmentBaseUrl, CustomTimeFrameOptions } from 'services/constants';
import { AppRoutes } from 'services/routes';
import { useGetIntakeCasesQuery } from 'store/api/intakeCaseApi';
import { useGetProductsMapQuery } from 'store/api/referenceApi';
import { type BenefitCheckPayload } from 'store/api/scopeAssessorApi';
import { useGetTreatmentsForPatientQuery } from 'store/api/treatmentApi';
import {
    type FormSections,
    type IntakeFormSectionSchemas,
} from 'store/intakeForm/intakeFormSchema';

const getEligibilityMessageAndDialogTitle = (
    benefitCheckResponse?: BenefitCheckPayload,
): { dialogTitle: string; eligibilityMessage: ReactElement } => {
    if (!benefitCheckResponse) {
        return {
            dialogTitle: 'Patient Not Found',
            eligibilityMessage: (
                <InlineMessage
                    wrapText
                    intent={InlineMessage.Intent.ERROR}
                    label="No patient found. Please check patient details. If they are correct, you can add this patient to the intake case."
                />
            ),
        };
    }
    return benefitCheckResponse.is_eligible
        ? {
              dialogTitle: 'Patient Found',
              eligibilityMessage: (
                  <InlineMessage
                      wrapText
                      intent={InlineMessage.Intent.SUCCESS}
                      label="Patient is eligible."
                  />
              ),
          }
        : {
              dialogTitle: 'Patient Not Eligible',
              eligibilityMessage: (
                  <InlineMessage
                      wrapText
                      intent={InlineMessage.Intent.WARNING}
                      label="Patient found but is not currently eligible. You can add this patient to the intake case."
                  />
              ),
          };
};
interface BenefitCheckDialogProps extends Required<
    Pick<
        ComponentProps<typeof Dialog>,
        'isOpen' | 'onPrimaryButtonClick' | 'onSecondaryButtonClick'
    >
> {
    benefitCheckResponse?: BenefitCheckPayload;
}

export const BenefitCheckDialog = ({
    benefitCheckResponse = undefined,
    isOpen,
    onPrimaryButtonClick,
    onSecondaryButtonClick,
}: BenefitCheckDialogProps) => {
    const formik =
        useFormikContext<
            Yup.InferType<
                (typeof IntakeFormSectionSchemas)[typeof FormSections.HEALTH_PLAN_AND_PATIENT]
            >
        >();

    const { data: treatments } = useGetTreatmentsForPatientQuery(formik.values, { skip: !isOpen });
    const { data: productsMap } = useGetProductsMapQuery(undefined, { skip: !isOpen });
    const { data: intakeCases } = useGetIntakeCasesQuery(
        {
            page: 0,
            timeframe: CustomTimeFrameOptions.ALL_TIME,
            intakeStatus: [
                IntakeStatusIndicator.Statuses.INCOMPLETE,
                IntakeStatusIndicator.Statuses.READY,
                IntakeStatusIndicator.Statuses.SUBMITTED,
            ],
            memberId: formik.values.memberId,
            memberFirstName: formik.values.memberFirstName,
            memberLastName: formik.values.memberLastName,
        },
        { skip: !isOpen },
    );

    const { eligibilityMessage, dialogTitle } =
        getEligibilityMessageAndDialogTitle(benefitCheckResponse);
    return (
        <Dialog
            dialogTitle={dialogTitle}
            isOpen={isOpen}
            primaryButtonLabel="Confirm Patient"
            secondaryButtonLabel="Return To Intake Form"
            onPrimaryButtonClick={onPrimaryButtonClick}
            isPrimaryButtonDisabled={!!formik.errors.benefitCheckPhone}
            onSecondaryButtonClick={onSecondaryButtonClick}
            dialogContent={
                <>
                    <div className="margin__bottom--medium">{eligibilityMessage}</div>
                    <FormSectionRow
                        rowContent={
                            <>
                                <DetailLabelValuePair
                                    label="Patient"
                                    value={formatNameLastFirst(
                                        formik.values.memberLastName,
                                        formik.values.memberFirstName,
                                    )}
                                />
                                <DetailLabelValuePair
                                    label="Date of Birth"
                                    value={formatDate(formik.values.memberDateOfBirth)}
                                />
                                <DetailLabelValuePair
                                    label="Member ID"
                                    value={formik.values.memberId}
                                />
                            </>
                        }
                    />
                    <FormSectionRow
                        rowContent={
                            <>
                                <DetailLabelValuePair
                                    label="Gender"
                                    value={formatGender(benefitCheckResponse?.gender)}
                                />
                                <AddressDetailLabelValue
                                    address1={benefitCheckResponse?.patient_address_line_1}
                                    address2={benefitCheckResponse?.patient_address_line_2}
                                    city={benefitCheckResponse?.member_city}
                                    state={benefitCheckResponse?.member_state}
                                    zip={benefitCheckResponse?.member_zip}
                                />
                            </>
                        }
                    />
                    <PhoneInputFormik id="benefitCheckPhone" label="Patient Phone Number" />
                    <div className="sds_benefit-check__section">
                        <div className="sds_benefit-check__section--title">Policy Information</div>
                        <FormSectionRow
                            rowContent={
                                <>
                                    <DetailLabelValuePair
                                        label="Account ID"
                                        value={benefitCheckResponse?.account}
                                    />
                                    <DetailLabelValuePair
                                        label="Effective Start Date"
                                        value={formatDate(
                                            benefitCheckResponse?.coverage_start_date,
                                        )}
                                    />
                                    <DetailLabelValuePair
                                        label="Effective End Date"
                                        value={formatDate(benefitCheckResponse?.coverage_end_date)}
                                    />
                                </>
                            }
                        />
                    </div>
                    <div className="sds_benefit-check__section">
                        <div className="sds_benefit-check__section--title">Treatments</div>
                        {(treatments ?? []).length === 0 ? (
                            <div className="sds_benefit-check__section--not-found">
                                No treatments found
                            </div>
                        ) : (
                            treatments?.map((treatment) => (
                                <FormSectionRow
                                    key={treatment.treatment_id}
                                    rowContent={
                                        <>
                                            <DetailLabelValuePair
                                                label="Product"
                                                value={getProductName(
                                                    productsMap,
                                                    treatment.product_service_oid,
                                                )}
                                            />
                                            <div>
                                                <DetailLabel label="Status" />
                                                <TreatmentStatusIndicator
                                                    status={treatment.treatment_status}
                                                />
                                            </div>
                                            <div style={{ alignSelf: 'flex-end' }}>
                                                <Hyperlink
                                                    navUrl={`${AgoraTreatmentBaseUrl(formik.values.payerId)}${treatment.treatment_id}`}
                                                    id="go-to-treatment-link"
                                                    openInNewTab
                                                    linkText={
                                                        <ActionButtonText
                                                            label="Go To Treatment"
                                                            rightIcon={IconNames.CHEVRON_RIGHT}
                                                        />
                                                    }
                                                />
                                            </div>
                                        </>
                                    }
                                />
                            ))
                        )}
                    </div>
                    <div className="sds_benefit-check__section">
                        <div className="sds_benefit-check__section--title">Intake Cases</div>
                        {(intakeCases?.intakeCases ?? []).length === 0 ? (
                            <div className="sds_benefit-check__section--not-found">
                                No open intake cases
                            </div>
                        ) : (
                            intakeCases?.intakeCases?.map((intakeCase) => (
                                <FormSectionRow
                                    key={intakeCase.intake_case_id}
                                    rowContent={
                                        <>
                                            <DetailLabelValuePair
                                                label="Product"
                                                value={getProductName(
                                                    productsMap,
                                                    intakeCase.product_service_oid,
                                                    intakeCase.is_brand,
                                                )}
                                            />
                                            <div>
                                                <DetailLabel label="Status" />
                                                <LighthouseIntakeStatusIndicator
                                                    status={intakeCase.intake_status}
                                                />
                                            </div>
                                            <div style={{ alignSelf: 'flex-end' }}>
                                                <Hyperlink
                                                    navUrl={AppRoutes.intakeCaseDetails(
                                                        intakeCase.intake_case_id,
                                                    )}
                                                    id="go-to-intake-case-link"
                                                    openInNewTab
                                                    linkText={
                                                        <ActionButtonText
                                                            label="Go To Intake Case"
                                                            rightIcon={IconNames.CHEVRON_RIGHT}
                                                        />
                                                    }
                                                />
                                            </div>
                                        </>
                                    }
                                />
                            ))
                        )}
                    </div>
                </>
            }
        />
    );
};
